import bodybuilder from "bodybuilder";
import { convertByFactors } from "./convertByFactors";
import { isSet as isImmutableSet } from "immutable";
import moment from "moment";
import { WidgetType } from "./filter-configs/widgetTypes";
import { laboratoryConversionFactors } from "scenes/patient/laboratory/config";
import _ from 'lodash';

const ruleHandlers = {
    number: numberHandler,
    string: matchHandler,
    boolean: termHandler,
    nested: nestedHandler,
    dateRange: dateRangeHandler,
    range: rangeHandler,
    tags: tagsHandler,
    switchWithSelect: switchWithSelectHandler,
    switchWithAdditionalSwitchQuestionAndSelect: switchWithAdditionalSwitchQuestionAndSelectHandler,
    switchWithSelectAndTags: switchWithSelectAndTagsHandler,
    switchWithNestedSelect: switchWithNestedSelectHandler,
    switchWithYearSelect: switchWithYearSelectHandler,
    numberOrSelect: numberOrSelectHandler,
};

const shouldProcessRule = ({ properties: { value, disabled, operator } }) => {
    return !disabled && value !== "" && (typeof value !== 'object' || !_.isEmpty(value)) && operator !== "";
};

const DOLLAR_SIGN_CLEANER = /\$[^$]*\$/g;

const mergeRuleWithQuery = (query, rule) => {
    if (rule.type === "group") {
        const groupRules = rule.properties.value;
        const andGroup = rule.properties.conjunction === "and";
        const positiveQueryType = andGroup ? "must" : "should";
        // IDK if this is the right way to handle this, but it works
        // my understanding of bodybuilder query library is limited
        const results = groupRules.map((rule) => mergeRuleWithQuery(bodybuilder(), rule).build().query).filter(Boolean); // For empty nested rules i get null should investigate why
        return query.filter("bool", { [rule.properties.exclude ? "must_not" : positiveQueryType]: results });
    } else {
        const ruleProperties = rule.properties;
        const ruleContext = rule.context || {};
        const value = ruleProperties.value;
        const handler = ruleHandlers[rule.properties.explicitFieldType || value.type || typeof value];
        return handler ? handler(query, ruleProperties, ruleContext) : query;
    }
};

function filterDisabledNodes(tree) {
    return tree.filter(shouldProcessRule).map((node) => {
        if (node.type === "group" && node.properties.value) {
            return {
                ...node,
                properties: {
                    ...node.properties,
                    value: filterDisabledNodes(node.properties.value),
                },
            };
        } else if (node.type === "rule" && node.properties.value && node.properties.value.type === "nested") {
            return {
                ...node,
                properties: {
                    ...node.properties,
                    value: {
                        ...node.properties.value,
                        nestedValues: filterDisabledNodes(node.properties.value.nestedValues),
                    },
                },
            };
        }
        return node;
    });
}

function buildElasticQuery(formTree) {
    const filteredTree = filterDisabledNodes(formTree).map((node) => ({
        ...node,
        properties: {
            ...node.properties,
            field: node.properties?.field?.replace(DOLLAR_SIGN_CLEANER, ""),
            nestedPath: node.properties?.nestedPath?.replace(DOLLAR_SIGN_CLEANER, ""),
        },
    }));
    return filteredTree.reduce(mergeRuleWithQuery, bodybuilder()).build();
}

function numberHandler(query, { value, field, operator }) {
    return operator === "eq" || operator === undefined
        ? query.filter("term", field, value)
        : query.filter("range", field, { [operator]: value });
}

function termHandler(query, { value, field, emptyAsFalse, ...other }) {
    if (value === false && emptyAsFalse) {
        return query.filter("bool", (b) => {
            b.orFilter("bool", "must_not", { exists: { field } });
            return b.orFilter("term", field, value);
        });
    } else {
        return query.filter("term", field, value);
    }
}

function matchHandler(query, { value, field, mustNotOnBang }){
    if(mustNotOnBang && value?.startsWith("!")){
        return query.notFilter("match", field, value?.replace("!", ""));
    } else {
        return query.filter("match", field, value);
    }
}

function tagsHandler(query, { value, field, emptyAsFalse }) {
    const tagsValueKey = value.type === "tags" ? "value" : "tagsValue";
    const unwrappedValue = value[tagsValueKey];
    const isSet = (v) => v instanceof Set || isImmutableSet(v);

    const toArray = (v) => (v instanceof Set ? Array.from(v) : isImmutableSet(v) ? v.toArray() : v);

    const normalizedArrayValue = isSet(unwrappedValue) ? toArray(unwrappedValue) : unwrappedValue;
    return query.filter(
        "bool",
        "must",
        normalizedArrayValue.map(
            (tag) =>
                bodybuilder()
                    .addFilter("term", field + "." + tag, true)
                    .build().query,
        ),
    );
}

function switchWithSelectAndTagsHandler(query, { value, field, emptyAsFalse }) {
    const queryWithSwitchWithSelect = switchWithSelectHandler(query, { value });
    const resultQuery = value.switchValue.value
        ? tagsHandler(queryWithSwitchWithSelect, { value, field, emptyAsFalse })
        : queryWithSwitchWithSelect;
    return resultQuery;
}

function switchWithSelectHandler(query, { value }) {
    return query.filter("bool", (b) => {
        b.filter("term", value.switchValue.field, value.switchValue.value);
        if (value.switchValue.value && value.selectValue.value) {
            //NOTE Akim: computed selectValueField is necessary because sometimes we have fields of multiple types like
            // keyword and integer (example anamnesis.haveCOVIDVaccinationTimes)
            const selectValueField = Number.isInteger(value.selectValue.value)
                ? value.selectValue.field + ".integer"
                : value.selectValue.field;
            b.filter("match", selectValueField, value.selectValue.value);
        }
        return b;
    });
}

function switchWithAdditionalSwitchQuestionAndSelectHandler(query, { value }) {
    return query.filter("bool", (b) => {
        b.filter("term", value.switchValue.field, value.switchValue.value);
        if (value.switchValue.value && value.selectValue.value) {
            b.filter("match", value.selectValue.field, value.selectValue.value);
        }
        if (value.switchValue.value) {
            b.filter("term", value.secondSwitchValue.field, value.secondSwitchValue.value);
        }
        return b;
    });
}

function switchWithYearSelectHandler(query, { value, yearOperator, numberOfEntriesOperator }) {
    // should get number of events and operator
    // then modify the query if needed
    return query.filter("bool", (b) => {
        b.filter("term", value.switchValue.field, value.switchValue.value);
        if (value.switchValue.value && yearOperator && value.yearValue.value) {
            if (yearOperator === "eq") {
                b.filter("range", value.yearValue.field, {
                    gte: value.yearValue.value,
                    lte: moment(value.yearValue.value).endOf("year").toISOString(),
                });
            } else {
                const yearValue =
                    yearOperator === "gt"
                        ? moment(value.yearValue.value).endOf("year").toISOString()
                        : value.yearValue.value;
                b.filter("range", value.yearValue.field, {
                    [yearOperator]: yearValue,
                });
            }
        }
        if (value.switchValue.value && numberOfEntriesOperator && value.numberOfEntriesValue.value) {
            if (numberOfEntriesOperator === "eq") {
                b.filter("term", value.numberOfEntriesValue.field, value.numberOfEntriesValue.value);
            } else {
                b.filter("range", value.numberOfEntriesValue.field, {
                    [numberOfEntriesOperator]: value.numberOfEntriesValue.value,
                });
            }
        }
        return b;
    });
}

function numberOrSelectHandler(query, { value, numOperator }) {
    const { numValue, selectValue, switchValue } = value;
    query.filter("bool", (b) => {
        b.filter("term", switchValue.field, switchValue.value);
        if (numValue.value) {
            if (numOperator === "eq") {
                b.orFilter("term", numValue.field, numValue.value);
            } else {
                b.orFilter("range", numValue.field, { [numOperator]: numValue.value });
            }
            b.orFilter("term", numValue.field, numValue.value);
        }
        if (selectValue.value) {
            b.orFilter("term", selectValue.field, selectValue.value);
        }
        b.filterMinimumShouldMatch(1, true);
        return b;
    });

    return query;
}

function switchWithNestedSelectHandler(query, { value, nestedPath }) {
    const a = bodybuilder().addFilter("term", value.switchValue.field, value.switchValue.value).build().query;
    const b = bodybuilder()
        .query("nested", { path: nestedPath }, (f) => {
            return f.query("term", value.selectValue.field, value.selectValue.value);
        })
        .build().query;
    if (value.switchValue.value && value.selectValue.value) {
        return query.filter("bool", "must", [a, b]);
    } else {
        return query.filter("term", value.switchValue.field, value.switchValue.value);
    }
}

function dateRangeHandler(query, { value, field }) {
    return value.to === "" && value.from === ""
        ? query
        : query.filter("range", field, {
              lte: value.from ? `now-${value.from}y/d` : undefined,
              gte: value.to ? `now-${value.to}y/d` : undefined,
          });
}

function rangeHandler(query, { value, field }) {
    return value.from === "" && value.to === ""
        ? query
        : query.filter("range", field, {
              gte: value.from ? value.from : undefined,
              lte: value.to ? value.to : undefined,
          });
}

//TODO LM: ??????
const contextualCriteriaHandler = (query, rule) => {
    const { value, field: criteriaName } = rule.properties;
    // TODO: remove magic strings
    // fields should come from config or be part of rule (maybe enriched by ruleContextEnricher)
    if (criteriaName === "medCriteria") {
        if (value === "NON_ACTIVE") {
            query.filter("exists", "medications.deletion_date");
        } else if (value === "ACTIVE") {
            query.notFilter("exists", "medications.deletion_date");
        }
    }
    return query;
};
/**
 * TODO LM: What? the? fuck?
 * - 'dynamicCriteriaRules' are rules that might depend on context, might use differed fields specified in code, etc.
 * - 'independentRules' are rules that are not dependent on context, they are just simple rules where field matches elasticsearch field
 */
const separateRulesByRulesWithContextAndNot = (ruleProperties) => {
    return ruleProperties.reduce(
        (acc, rule) => {
            const { virtualField } = rule.properties;
            const key = virtualField ? "dynamicCriteriaRules" : "independentRules";
            return {
                ...acc,
                [key]: acc[key].concat(rule),
            };
        },
        { dynamicCriteriaRules: [], independentRules: [] },
    );
};

const LAB_CONVERSION_CONFIG = {
    hemoglobin: { standardUnit: "mmolperl", factors: laboratoryConversionFactors.hemoglobin },
    kreatinin: { standardUnit: "mikromolperl", factors: laboratoryConversionFactors.kreatinin },
    crp: { standardUnit: "mgperl", factors: laboratoryConversionFactors.crp },
    totalcholesterol: { standardUnit: "mmolperl", factors: laboratoryConversionFactors.cholesterol },
    ldlcholesterol: { standardUnit: "mmolperl", factors: laboratoryConversionFactors.cholesterol },
    hdlcholesterol: { standardUnit: "mmolperl", factors: laboratoryConversionFactors.cholesterol },
};

const convertLabValues = (nestedRulesToProcess) => {
    const labNameRule = nestedRulesToProcess.find((r) => r.properties.labName);
    if (!labNameRule) {
        return nestedRulesToProcess;
    }

    const labName = labNameRule.properties.labName;
    const config = LAB_CONVERSION_CONFIG[labName];

    const unitRule = nestedRulesToProcess.find((r) => r.properties.field === `labs.${labName}_unit`);
    const selectedUnit = unitRule?.properties?.value;

    const processedRules = nestedRulesToProcess
        .filter((r) => r.properties.field !== `labs.${labName}_unit`)
        .map((r) => {
            if (r.properties.field === "labs.value_in_standard_unit" && config && selectedUnit) {
                const convertFn = convertByFactors(config.factors);
                return {
                    ...r,
                    properties: {
                        ...r.properties,
                        value: convertFn(r.properties.value, selectedUnit, config.standardUnit),
                    },
                };
            }
            return r;
        });

    processedRules.push({
        type: "rule",
        properties: {
            field: "labs.name",
            operator: "eq",
            value: labName.toLowerCase(),
        },
    });

    return processedRules;
};

//TODO LM: Nested switch will only work if the boolean we are "switching" on is also nested (like haveSurgery in cancerdto)
// if it ain't it won't because the converter will search for it in the nested path.
// So if we want to nest a top level boolean ("haveActiveCancer", "haveCardiacArrhythmia", etc) we need this function
// which extracts it as a top level term. It will only work on the first node with widgetType = "switch"
// Yes, this is a shit solution. I want to transform the query generation to be declared with the renderer.
// function extractFirstEmbeddedSwitcheAsTopLevelTerm(query, rules) {
//     const filteredIndependentRules = rules.filter((rule) => rule?.properties?.widgetType !== WidgetType.SWITCH);
//     const embeddedSwitchField = rules.find((rule) => rule?.properties?.widgetType === WidgetType.SWITCH);
//     const queryWithProcessedSwitch = embeddedSwitchField
//         ? query.filter("bool", (b) => {
//               b.filter(
//                   "term",
//                   embeddedSwitchField.properties.field.replace(DOLLAR_SIGN_CLEANER, ""),
//                   embeddedSwitchField.properties.value,
//               );
//               return b;
//           })
//         : query;
//     return {
//         queryWithProcessedSwitch,
//         filteredIndependentRules,
//     };
// }

function extractFirstEmbeddedSwitcheAsTopLevelTerm(query, rules) {
    const filteredIndependentRules = rules.filter((rule) => rule?.properties?.widgetType !== WidgetType.SWITCH);
    const embeddedSwitchFields = rules.filter((rule) => rule?.properties?.widgetType === WidgetType.SWITCH);
    const queryWithProcessedSwitch = embeddedSwitchFields?.length > 0
        ? embeddedSwitchFields.reduce((accumulatedQuery, currentSwitchField)=>{
            return accumulatedQuery.filter("bool", (b) => {
                b.filter(
                    "term",
                    currentSwitchField.properties.field.replace(DOLLAR_SIGN_CLEANER, ""),
                    currentSwitchField.properties.value,
                );
                return b;
            })
        }, query)
        : query;
    return {
        queryWithProcessedSwitch,
        filteredIndependentRules,
    };
}

function nestedHandler(query, { value, field, exclude, nestedPath }) {
    const nestedFieldPath = nestedPath || field;
    const nestedRulesToProcess = value.nestedValues.filter(shouldProcessRule);
    const enrichedNestedRules = convertLabValues(nestedRulesToProcess);
    const { dynamicCriteriaRules, independentRules } = separateRulesByRulesWithContextAndNot(enrichedNestedRules);

    const { queryWithProcessedSwitch, filteredIndependentRules } = extractFirstEmbeddedSwitcheAsTopLevelTerm(
        query,
        independentRules,
    );

    const medCriteriaNeverSubscribedIsPresent = dynamicCriteriaRules.some(
        (rule) => rule.properties.field === "medCriteria" && rule.properties.value === "NEVER_SUBSCRIBED",
    );

    // exclusive or (XOR) so if exclude is true we want to invert the medCriteriaNeverSubscribedIsPresent
    //TODO LM: Wild bitwise operator appears! Its super effective! Lets add unsigned right shift! Because I HATE THAT TOO!!!
    const notQuery = exclude ^ medCriteriaNeverSubscribedIsPresent;
    return filteredIndependentRules.length === 0 || filteredIndependentRules.every((v) => v.properties.value === "")
        ? queryWithProcessedSwitch
        : queryWithProcessedSwitch[notQuery ? "notQuery" : "query"]("nested", { path: nestedFieldPath }, (f) => {
              return f.query("bool", (b) => {
                  const queryWithProcessedDynamicCriteriaRules = dynamicCriteriaRules.reduce(
                      contextualCriteriaHandler,
                      b,
                  );
                  return filteredIndependentRules.reduce(mergeRuleWithQuery, queryWithProcessedDynamicCriteriaRules);
              });
          });
}

export default buildElasticQuery;
